import React from 'react';
import { StatProps } from '../../../types/DesignSystem/dataviz';
import Typography from '../Typography/Typography';
import { withClassName } from '../cn';

/**
 * Stat (§ 311) — a figure with its name and its comparison.
 *
 * **The label goes above the value, and it is `sub-title-label` (12/600), not a
 * heading.** A KPI tile is scanned, not read: the eye lands on the number and only
 * then asks what it is. Setting the label at heading weight inverts that — the tile
 * reads as a section with a number in it. It also keeps `Stat` usable in a row of
 * six without six competing headings, which is the arrangement it exists for.
 *
 * **The value is `display` (24/600) at `md`.** Same token as a page title, which is
 * deliberate: on a dashboard the figures ARE the page-level moment, and § 285 spent
 * that rung on exactly this kind of thing. `sm` drops to `heading-3` (16/600) for a
 * summary bar above a table, where the numbers must not out-shout the page's own
 * title two rows up.
 *
 * **It takes `value` as a string, not a number.** Locale, currency, unit and rounding
 * are the caller's — a component that formatted them would be wrong in half the
 * places it is used, and `1,284` / `97.4%` / `4.1s` have nothing in common to format.
 */

const VALUE_VARIANT = {
  sm: 'heading-3',
  md: 'display',
} as const;

const Stat = React.forwardRef<HTMLDivElement, StatProps>(function Stat({
  label,
  value,
  caption,
  trend,
  size = 'md',
  className = '',
  'data-testid': dataTestId,
  ...rest
}, ref) {
  return (
    <div
      {...rest}
      ref={ref}
      data-testid={dataTestId}
      className={withClassName(`tw-flex tw-flex-col tw-gap-1`, className)}
    >
      <Typography
        variant="sub-title-label"
        className="tw-text-ink-tertiary"
      >
        <span data-testid={dataTestId ? `${dataTestId}-label` : undefined}>{label}</span>
      </Typography>

      <Typography variant={VALUE_VARIANT[size]} as="p" className="tw-text-ink tw-tabular-nums">
        {/* tabular-nums on the value is not decoration: a row of Stats whose figures
            change on a poll would judder by a fraction of a character every tick,
            and in a column of tiles the digits would not line up at all. */}
        <span data-testid={dataTestId ? `${dataTestId}-value` : undefined}>{value}</span>
      </Typography>

      {trend && <div className="tw-mt-0.5">{trend}</div>}

      {caption && (
        <div
          data-testid={dataTestId ? `${dataTestId}-caption` : undefined}
          className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap tw-text-caption-metadata tw-text-ink-tertiary"
        >
          {caption}
        </div>
      )}
    </div>
  );
});

export default Stat;
